import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CandidateService } from "@/services/candidateService";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { CheckCircle, XCircle } from "lucide-react";
import { classifyConfirmationOutcome } from "@/utils/confirmationOutcome";
import { useCountdown } from "@/hooks/useCountdown";

const CandidateConfirmation = ({ flow }) => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [errorCode, setErrorCode] = useState(null);
  const [resend, setResend] = useState({ status: "idle" });
  const { secondsLeft, start: startCountdown } = useCountdown();

  // One entry per flow this page serves. `resend` is null where the flow has no
  // resend endpoint — jobfit-apply mints its link from the recruiter's action,
  // so there is nothing for the candidate to ask again for.
  const FLOW_ACTIONS = {
    "resume-reupload": {
      finalize: CandidateService.finalizeResumeReuploadConfirmation,
      resend: CandidateService.resendResumeReuploadConfirmation,
    },
    "jobfit-apply": {
      finalize: CandidateService.finalizeJobFitApplyConfirmation,
      resend: null,
    },
  };

  const { finalize: finalizeFn, resend: resendFn } = FLOW_ACTIONS[flow] ?? {
    finalize: CandidateService.finalizeCandidateConfirmation,
    resend: CandidateService.resendCandidateConfirmation,
  };

  useEffect(() => {
    const confirmApplication = async () => {
      if (!token) {
        setSuccess(false);
        setMessage("Invalid confirmation link.");
        setLoading(false);
        return;
      }

      try {
        const response = await finalizeFn(token);

        setSuccess(true);
        setMessage(
          response?.message ||
            "Your application has been confirmed successfully."
        );
      } catch (error) {
        const code = error?.response?.data?.error_code;
        const outcome = classifyConfirmationOutcome(code);

        setErrorCode(code);
        setSuccess(outcome.status === "success");
        setMessage(
          error?.response?.data?.message ||
            (outcome.status === "success" ? "This application has already been confirmed." : "Confirmation failed."),
        );
      } finally {
        setLoading(false);
      }
    };

    confirmApplication();
  }, []);

  const outcome = classifyConfirmationOutcome(errorCode);
  // Once a resend has actually gone out, there is nothing left to offer —
  // no button, just the confirmation that a new link is on its way.
  const showResendButton = !success && Boolean(resendFn) && outcome.canResend && resend.status !== "sent" && resend.status !== "limit-reached";
  const resendDisabled = resend.status === "sending" || secondsLeft > 0;
  // Recoverable staged work, but this flow has no resend endpoint to recover
  // it with — jobfit-apply links are minted from the recruiter's action.
  // Without this the candidate reads "expired" and is offered nothing.
  const showRecruiterHint = !success && !resendFn && outcome.canResend;

  const handleResend = async () => {
    if (!token) return;
    setResend({ status: "sending" });
    try {
      const res = await resendFn(token);
      setResend({
        status: "sent",
        message: res?.message,
        emailMasked: res?.email_masked,
        alternateEmailMasked: res?.alternate_email_masked,
        sendsRemaining: res?.sends_remaining,
      });
    } catch (error) {
      const code = error?.response?.data?.error_code;
      if (code === "CONFIRMATION_RESEND_TOO_SOON") {
        startCountdown(error?.response?.data?.data?.retry_after_seconds || 60);
        setResend({ status: "too-soon", message: error?.response?.data?.message || "Please wait before requesting another link." });
      } else if (code === "CONFIRMATION_RESEND_LIMIT_REACHED") {
        setResend({ status: "limit-reached", message: error?.response?.data?.message || "Please start again." });
      } else {
        setResend({ status: "error", message: error?.response?.data?.message || "Couldn't send a new link. Please try again." });
      }
    }
  };

  return (
   <div className="min-h-[75vh] flex items-center justify-center px-4 sm:px-6 bg-background-primary">
      <Card className="max-w-md w-full p-8 text-center">
        {loading ? (
          <>
            <div className="loader mx-auto mb-4" />
            <h2 className="text-lg font-semibold">
              Confirming your application...
            </h2>
          </>
        ) : success ? (
          <>
            <CheckCircle
              size={64}
              className="mx-auto text-green-500 mb-4"
            />

            <h2 className="text-xl text-text-primary font-semibold mb-2">
              Application Confirmed
            </h2>

            <p className="text-text-secondary">
              {message}
            </p>
          </>
        ) : (
          <>
            <XCircle
              size={64}
              className="mx-auto text-red-500 mb-4"
            />

            <h2 className="text-xl text-text-primary font-semibold mb-2">
              Confirmation Failed
            </h2>

            <p className="text-text-secondary">
              {message}
            </p>

            {showRecruiterHint && (
              <p className="mt-4 text-xs text-text-secondary">
                Ask the recruiter you have been speaking to for a new link.
              </p>
            )}

            {showResendButton && (
              <div className="mt-4 space-y-2">
                <Button
                  onClick={handleResend}
                  disabled={resendDisabled}
                  className="bg-button-secondary text-text-base rounded-lg px-4 py-2 text-xs"
                >
                  {resend.status === "sending" ? "Sending..." : secondsLeft > 0 ? `Try again in ${secondsLeft}s` : "Resend confirmation link"}
                </Button>

                {(resend.status === "too-soon" || resend.status === "error") && <p className="text-xs text-status-error">{resend.message}</p>}
              </div>
            )}

            {resend.status === "sent" && (
              <p className="mt-4 text-xs text-text-secondary">
                {resend.message}
                {[resend.emailMasked, resend.alternateEmailMasked].filter(Boolean).length > 0
                  ? ` (${[resend.emailMasked, resend.alternateEmailMasked].filter(Boolean).join(" and ")})`
                  : ""}
              </p>
            )}

            {resend.status === "limit-reached" && <p className="mt-3 text-xs text-status-error">{resend.message}</p>}
          </>
        )}
      </Card>
    </div>
  );
};

export default CandidateConfirmation;
